import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Icon from '@amzn/meridian/icon';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import { useTranslation } from 'src/arb-wrapper';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import { COUNTRY_TO_REGION } from 'src/constants';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import type { BusinessUnitDto, Country } from 'src/store/generated/leela-api-generated';
import {
  useBusinessUnitAdminControllerFindGroupsQuery,
  useBusinessUnitAdminControllerFindMembersQuery,
  useBusinessUnitAdminControllerFindPartnersQuery,
} from 'src/store/leela-api';
import { formatUtcDate } from 'src/utils/formatDateTime';
import { GroupRoleLabelKeys } from 'src/utils/groupRoles';
import type { GroupRole } from 'src/utils/groupRoles';
import { GroupStatusLabelKeys, STATUS_TAG_TYPE } from 'src/utils/groupStatus';
import type { GroupStatus } from 'src/utils/groupStatus';
import { EditGroupModal } from '../access-management/EditGroupModal';

interface BuRowProps {
  bu: BusinessUnitDto;
  onEdit: () => void;
}

export const BuRow: React.FC<BuRowProps> = ({ bu, onEdit }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);

  const { data: groupsData } = useBusinessUnitAdminControllerFindGroupsQuery(
    { businessUnitId: bu.id, page: 1, pageSize: PERMISSION_LIST_PAGE_SIZE },
    { skip: !hasExpanded },
  );

  const { data: partnersData } = useBusinessUnitAdminControllerFindPartnersQuery(
    { id: bu.id, page: 1, pageSize: PERMISSION_LIST_PAGE_SIZE },
    { skip: !hasExpanded },
  );

  const partnerNameMap = new Map(
    ((partnersData?.data ?? []) as Array<{ id: string; name: string }>).map(partner => [partner.id, partner.name]),
  );

  const handleToggle = () => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(!isExpanded);
  };

  const regions = Array.from(new Set(
    (bu.countries ?? []).map(country => COUNTRY_TO_REGION.get(country)).filter((region): region is string => !!region),
  ));

  return (
    <Box data-testid={`bu-row-${bu.id}`} spacingInset="400" type="outline">
      <Column spacing="300">
        <div
          aria-expanded={isExpanded}
          data-testid={`bu-row-toggle-${bu.id}`}
          role="button"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (event.currentTarget === event.target && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              handleToggle();
            }
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
            <Text data-testid={`bu-row-name-${bu.id}`}>{bu.name}</Text>
            {regions.map(region => (
              <Tag key={region} type="neutral">{region}</Tag>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              aria-label={t('bu_management_edit_aria_label')}
              data-testid={`bu-row-edit-${bu.id}`}
              propagateClickEvent={false}
              type="icon"
              onClick={onEdit}
            >
              <Icon tokens={editTokens} />
            </Button>
          </div>
        </div>

        {isExpanded && (
          <Box backgroundColor="subdued" spacingInset="400">
            <Column data-testid={`bu-row-details-${bu.id}`} spacing="200">
              <Divider />
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_admins_label')}</Text>
                <Text>{bu.admins?.join(', ') || t('bu_management_not_available')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_regions_label')}</Text>
                <Text>{regions.join(', ') || t('bu_management_not_available')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_countries_label')}</Text>
                <Text>{bu.countries?.join(', ') || t('bu_management_not_available')}</Text>
              </Row>

              <Divider />
              <Text>{t('bu_management_permission_groups')}</Text>
              {groupsData?.data?.length
                ? (
                <Column spacing="200">
                  {(groupsData.data as Array<{ id: string; name: string; status: GroupStatus; role: GroupRole; countries?: string[]; publishers?: string[]; businessLines?: string[]; partnerId?: string; dataVisibilityPolicy?: string }>).map(group => (
                    <BuGroupSubRow businessUnitCountries={bu.countries ?? []} group={group} key={group.id} partnerNameMap={partnerNameMap} />
                  ))}
                </Column>
                  )
                : (
                <Text color="secondary" type="b200">{t('bu_management_no_groups')}</Text>
                  )}
            </Column>
          </Box>
        )}
      </Column>
    </Box>
  );
};

interface BuGroupSubRowProps {
  group: {
    id: string;
    name: string;
    status: GroupStatus;
    role: GroupRole;
    countries?: string[];
    publishers?: string[];
    businessLines?: string[];
    partnerId?: string;
    dataVisibilityPolicy?: string;
  };
  businessUnitCountries: string[];
  partnerNameMap: Map<string, string>;
}

const BuGroupSubRow: React.FC<BuGroupSubRowProps> = ({ group, businessUnitCountries, partnerNameMap }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasExpanded, setHasExpanded] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: membersData, isLoading } = useBusinessUnitAdminControllerFindMembersQuery(
    { id: group.id, page: 1, pageSize: PERMISSION_LIST_PAGE_SIZE },
    { skip: !hasExpanded },
  );

  const handleToggle = () => {
    if (!hasExpanded) setHasExpanded(true);
    setIsExpanded(!isExpanded);
  };

  const members = (membersData?.data ?? []) as Array<{ id: string; userId: string; expiresAt?: string }>;
  const regions = Array.from(new Set(
    (group.countries ?? [])
      .map(country => COUNTRY_TO_REGION.get(country as Country))
      .filter((region): region is string => !!region),
  ));

  return (
    <Box spacingInset="300" type="outline">
      <Column spacing="200">
        <div
          aria-expanded={isExpanded}
          data-testid={`bu-group-row-${group.id}`}
          role="button"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(event) => {
            if (event.currentTarget === event.target && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              handleToggle();
            }
          }}
        >
          <div
            data-testid={`bu-group-name-container-${group.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}
          >
            <Icon tokens={isExpanded ? chevronDownSmallTokens : chevronRightSmallTokens} />
            <ShrinkableTextValue testId={`bu-group-name-${group.id}`}>
              {group.name}
            </ShrinkableTextValue>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag type={STATUS_TAG_TYPE[group.status]}>
              {t(GroupStatusLabelKeys[group.status])}
            </Tag>
            <Button
              aria-label={t('bu_management_group_edit_aria_label')}
              data-testid={`bu-group-edit-${group.id}`}
              propagateClickEvent={false}
              type="icon"
              onClick={() => { setIsEditOpen(true); }}
            >
              <Icon tokens={editTokens} />
            </Button>
          </div>
        </div>

        {isExpanded && (
          <Box backgroundColor="subdued" spacingInset="300">
            <Column spacing="200">
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_role_label')}</Text>
                <Text>{GroupRoleLabelKeys[group.role] ? (t(GroupRoleLabelKeys[group.role]) ?? group.role) : group.role}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_regions_label')}</Text>
                <Text>{regions.length > 0 ? regions.join(', ') : t('bu_management_all')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_countries_label')}</Text>
                <Text>{group.countries?.length ? group.countries.join(', ') : t('bu_management_all')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_publishers_label')}</Text>
                <Text>{group.publishers?.length ? group.publishers.join(', ') : t('bu_management_all')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_business_lines_label')}</Text>
                <Text>{group.businessLines?.length ? group.businessLines.join(', ') : t('bu_management_all')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_partner_label')}</Text>
                <Text>{group.partnerId ? (partnerNameMap.get(group.partnerId) ?? group.partnerId) : t('bu_management_internal')}</Text>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('bu_management_data_policy_label')}</Text>
                <Text>{group.dataVisibilityPolicy ?? t('bu_management_not_available')}</Text>
              </Row>

              <Divider />
              <Text>{t('bu_management_members')}</Text>
              {isLoading && <Loader />}
              {!isLoading && members.length === 0 && (
                <Text color="secondary" type="b200">{t('bu_management_no_members')}</Text>
              )}
              {members.map(member => (
                <Row key={member.id} spacing="200">
                  <Text>{member.userId}</Text>
                  {member.expiresAt && (
                    <Text color="secondary" type="b200">
                      {t('bu_management_member_expires', {
                        date: formatUtcDate(
                          member.expiresAt,
                          t('bu_management_not_available'),
                        ),
                      })}
                    </Text>
                  )}
                </Row>
              ))}
            </Column>
          </Box>
        )}
      </Column>

      <EditGroupModal
        businessUnitCountries={businessUnitCountries}
        group={{ ...group, countries: group.countries ?? [], publishers: group.publishers ?? [], businessLines: group.businessLines ?? [] }}
        open={isEditOpen}
        partnerName={group.partnerId ? partnerNameMap.get(group.partnerId) : undefined}
        onClose={() => { setIsEditOpen(false); }}
      />
    </Box>
  );
};
